const DEFAULT_SUSPEND_REASON = 'default';

interface SmartSortRotatorOptions<T> {
  /**
   * Fetch the next row. Receives the row currently on screen (derive your
   * exclusion list from it — e.g. its creative ids) and an AbortSignal that
   * fires when the fetch is superseded, paused, or the rotator is destroyed.
   */
  fetchNextSet: (args: {
    current: T | undefined;
    signal: AbortSignal;
  }) => Promise<T>;
  /**
   * Whether a fetched row has anything to display. Drives no-fill handling: a
   * row for which this returns false keeps the last good row on screen and
   * retries next cycle instead of swapping in an empty row — or stops rotation
   * entirely when `stopOnEmpty` is set.
   */
  hasItems: (row: T) => boolean;
  /** Milliseconds between rotations. */
  intervalMs: number;
  /**
   * Max consecutive fetch failures tolerated before rotation stops
   * permanently. Omit to retry indefinitely. Resets after any success.
   */
  maxConsecutiveErrors?: number;
  /**
   * Cap for the exponential backoff on failed fetches. Defaults to
   * `max(intervalMs, 60000)`. A value below `intervalMs` is floored to
   * `intervalMs` so retries never fire faster than the rotation cadence.
   */
  maxRetryDelayMs?: number;
  /** Called when a rotation fetch rejects. Rotation continues (retries next cycle). */
  onError?: (error: unknown) => void;
  /**
   * Optional initial row for an immediate first paint. Omit and start()
   * fetches first. Note the asymmetry with fetched no-fill rows: an empty
   * seed (one for which `hasItems` is false) never starts rotation — no fetch
   * is issued and no error surfaces — whereas an empty row *returned by a
   * fetch* keeps the last good row and retries next cycle. Pass a seed only
   * when it has items; otherwise omit it and let start() fetch.
   */
  row?: T;
  /**
   * Stop rotation permanently the first time a fetch returns a row with no
   * items (per `hasItems`), keeping the last good row on screen. Defaults to
   * false — the last good row is kept and the next cycle retries. Once stopped,
   * resume()/start() are no-ops; a fresh instance is required to rotate again.
   */
  stopOnEmpty?: boolean;
}

export interface SmartSortRotatorSnapshot<T> {
  /** True when the most recent rotation fetch rejected. */
  isError: boolean;
  /** True while a rotation fetch is in flight. */
  isFetching: boolean;
  /** The row currently on screen; the last good row is kept on error/empty. */
  row?: T;
}

/**
 * Framework-agnostic controller that rotates a row on an interval: it re-fetches
 * (handing the current row to the caller so exclusions can be derived), keeps
 * the last good row when a fetch returns nothing (or stops, with `stopOnEmpty`),
 * and can be paused/resumed. It owns timers and request cancellation. State is
 * exposed via subscribe + getSnapshot so it can back a React
 * useSyncExternalStore or any observer.
 */
export class SmartSortRotator<T> {
  private consecutiveErrors = 0;
  private controller: AbortController | null = null;
  /** When the armed timer is due to fire; null while no timer is pending. */
  private dueAtMs: number | null = null;
  private readonly listeners = new Set<() => void>();
  private resumeDelayMs: number | undefined;
  private snapshot: SmartSortRotatorSnapshot<T>;
  private stopped = false;
  /** Rotation is suspended while any reason remains — see {@link pause}. */
  private readonly suspensions = new Set<string>();
  private timer: null | ReturnType<typeof setTimeout> = null;

  constructor(private readonly opts: SmartSortRotatorOptions<T>) {
    this.snapshot = { isError: false, isFetching: false, row: opts.row };
  }

  /**
   * Stops the rotator permanently and drops every subscriber, notifying them of
   * the resulting `isFetching: false` first. Call before unsubscribing if you
   * rely on that last notification. A subscriber cannot revive the rotator from
   * it — pause/resume no-op once stopped.
   */
  destroy(): void {
    this.stopped = true;
    this.teardown();
    this.listeners.clear();
  }

  getSnapshot = (): SmartSortRotatorSnapshot<T> => this.snapshot;

  /**
   * Suspends rotation and abandons any in-flight fetch. The first outstanding
   * pause banks the remainder of the interval so repeated pause/resume — a cursor
   * grazing a hovered row — cannot postpone rotation forever.
   *
   * Rotation stays suspended until every `reason` is resumed, so independent
   * callers (a hover handler and a visibility gate) can't clobber each other.
   */
  pause(reason: string = DEFAULT_SUSPEND_REASON): void {
    if (this.stopped || this.suspensions.has(reason)) {
      return;
    }
    if (this.suspensions.size === 0) {
      this.resumeDelayMs = this.remainingDelayMs();
    }
    this.suspensions.add(reason);
    this.teardown();
  }

  /** Clears one pause reason; rotation restarts once none are left. */
  resume(reason: string = DEFAULT_SUSPEND_REASON): void {
    if (this.stopped || !this.suspensions.delete(reason)) {
      return;
    }
    if (this.suspensions.size > 0) {
      return;
    }
    const delayMs = this.resumeDelayMs;
    this.resumeDelayMs = undefined;
    this.scheduleNext(delayMs);
  }

  start(): void {
    if (this.stopped) {
      return;
    }
    this.scheduleNext();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private hasItems(row: T | undefined): boolean {
    return row !== undefined && this.opts.hasItems(row);
  }

  private isStale(signal?: AbortSignal): boolean {
    return (
      (signal?.aborted ?? false) || this.stopped || this.suspensions.size > 0
    );
  }

  /**
   * What is left of the pending interval. Zero mid-fetch (that interval already
   * elapsed); undefined with no timer armed and no fetch running — pause()
   * before start() — so resume() falls back to the normal cadence.
   */
  private remainingDelayMs(): number | undefined {
    if (this.dueAtMs !== null) {
      return Math.max(0, this.dueAtMs - Date.now());
    }
    return this.snapshot.isFetching ? 0 : undefined;
  }

  private retryDelayMs(): number {
    const { intervalMs } = this.opts;
    const cap = this.opts.maxRetryDelayMs ?? Math.max(intervalMs, 60_000);
    const backoff = intervalMs * 2 ** (this.consecutiveErrors - 1);
    return Math.max(intervalMs, Math.min(backoff, cap));
  }

  private schedule(delayMs: number = this.opts.intervalMs): void {
    this.clearTimer();
    if (this.isStale()) {
      return;
    }
    this.dueAtMs = Date.now() + delayMs;
    this.timer = setTimeout(() => {
      this.dueAtMs = null;
      void this.tick();
    }, delayMs);
  }

  private scheduleNext(delayMs?: number): void {
    if (this.snapshot.row === undefined) {
      this.schedule(0);
    } else if (this.hasItems(this.snapshot.row)) {
      this.schedule(delayMs);
    }
  }

  private setState(patch: Partial<SmartSortRotatorSnapshot<T>>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }

  private teardown(): void {
    this.clearTimer();
    this.dueAtMs = null;
    this.controller?.abort();
    if (this.snapshot.isFetching) {
      this.setState({ isFetching: false });
    }
  }

  private async tick(): Promise<void> {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    this.setState({ isError: false, isFetching: true });

    let next: T;
    try {
      next = await this.opts.fetchNextSet({
        current: this.snapshot.row,
        signal: controller.signal,
      });
    } catch (error) {
      if (this.isStale(controller.signal)) {
        return;
      }
      this.consecutiveErrors += 1;
      this.opts.onError?.(error);
      this.setState({ isError: true, isFetching: false });
      const { maxConsecutiveErrors } = this.opts;
      if (
        maxConsecutiveErrors != null &&
        this.consecutiveErrors >= maxConsecutiveErrors
      ) {
        this.stopped = true;
        return;
      }
      this.schedule(this.retryDelayMs());
      return;
    }

    if (this.isStale(controller.signal)) {
      return;
    }
    this.consecutiveErrors = 0;
    if (this.hasItems(next)) {
      this.setState({ isFetching: false, row: next });
    } else {
      this.setState({ isFetching: false });
      if (this.opts.stopOnEmpty) {
        this.stopped = true;
        return;
      }
    }
    this.schedule();
  }
}
